import React, { useEffect, useState } from 'react';
import Logo from './Logo';
import KnowledgePanel from './KnowledgePanel';
import SimplifiedTier1Workspace from './SimplifiedTier1Workspace';
import {
  canFeedLlm,
  canManageKbNoindex,
  canSyncKb,
  getFeedFileAccept,
  isOrgAdminRole,
  isSuperadminRole,
} from '../constants';
import { tUi } from '../utils/uiText';

function formatHistoryTimestamp(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function SimplifiedTier1App({
  authProfile,
  onLogout,
  uiLanguage,
  showHistory,
  setShowHistory,
  chatHistory,
  historyLoading,
  sessionId,
  onSelectConversation,
  onDeleteConversation,
  onClearHistory,
  onNewConversation,
  startingNewChat = false,
  openAdminPortal,
  isSuperadmin,
  apiFetch,
  error,
  setError,
  canSwitchProductMode,
  entitledModeOptions,
  onConsultantModeChange,
  savedReportHtml,
  savedReportTitle,
  savedUploadedFiles,
  onUploadedFilesChange,
  savedTier1Draft,
  onTier1DraftChange,
  savedReportChatMessages = [],
  onReportChatMessagesChange,
  onConversationRefresh,
  screenLanguage,
  reportLanguage,
  onScreenLanguageChange,
  onReportLanguageChange,
  onSaveLanguages,
  onReportHtmlUpdated,
  onRefreshKbStats,
  kbStats,
  kbSyncing,
  onKbSync,
  onKbReindex,
  ingestUrl,
  onIngestUrlChange,
  onIngestUrl,
  ingestingUrl,
  onKbUpload,
  kbUploading,
  kbUploadFeedback,
  kbUploadVisible,
  onKbUploadVisibleChange,
  onKbNoindexToggle,
  onKbDelete,
  kbNoindexUpdatingPath,
  kbDeletingPath,
}) {
  const [analysisRunning, setAnalysisRunning] = useState(false);
  const [showNewChatConfirm, setShowNewChatConfirm] = useState(false);
  const [showKb, setShowKb] = useState(false);

  useEffect(() => {
    onRefreshKbStats?.();
    // Refresh KB stats when opening Tier 1 workspace.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNewChatClick = () => {
    if (startingNewChat) return;
    if (analysisRunning) {
      setShowNewChatConfirm(true);
      return;
    }
    onNewConversation?.();
  };

  const handleConfirmNewChat = () => {
    setShowNewChatConfirm(false);
    onNewConversation?.();
  };

  return (
    <div className="app-shell bg-legend-deep text-legend-paper text-sm">
      <header className="legend-hairline bg-legend-navy shrink-0 app-top-bar">
        <div className="container mx-auto px-4 py-2 flex items-center justify-between gap-2">
          <Logo modeId="nation_state" />
          <div className="flex items-center gap-1.5 overflow-x-auto shrink min-w-0">
            {canSwitchProductMode && entitledModeOptions?.length > 1 && (
              <select
                value="nation_state"
                onChange={(e) => onConsultantModeChange?.(e.target.value)}
                className="px-2 py-1.5 bg-legend-deep border border-legend-teal/30 rounded-lg text-xs text-legend-paper"
                aria-label="Product mode"
              >
                {entitledModeOptions.map((mode) => (
                  <option key={mode.id} value={mode.id}>
                    {mode.label}
                  </option>
                ))}
              </select>
            )}
            {isSuperadmin && (
              <button
                type="button"
                onClick={openAdminPortal}
                className="px-2.5 py-1.5 bg-legend-deep hover:bg-legend-navy border border-legend-teal/30 text-legend-teal-tint rounded-lg text-xs"
              >
                Admin
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowKb((v) => !v)}
              className="px-2.5 py-1.5 bg-legend-deep hover:bg-legend-navy border border-legend-teal/30 text-legend-teal-tint rounded-lg text-xs"
            >
              {showKb ? tUi(uiLanguage, 'kbHidePanel') : tUi(uiLanguage, 'kbShowPanel')}
            </button>
            <button
              type="button"
              onClick={() => setShowHistory((v) => !v)}
              className="px-2.5 py-1.5 bg-legend-deep hover:bg-legend-navy border border-legend-teal/30 text-legend-teal-tint rounded-lg text-xs"
            >
              {showHistory ? 'Hide History' : 'History'}
            </button>
            <button
              type="button"
              onClick={handleNewChatClick}
              disabled={startingNewChat}
              className="px-2.5 py-1.5 bg-legend-teal hover:bg-legend-teal-dark disabled:bg-legend-deep text-white font-medium rounded-lg text-xs transition-all duration-200 flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>{startingNewChat ? 'Starting…' : 'New Chat'}</span>
            </button>
            <button
              type="button"
              onClick={onLogout}
              className="px-2.5 py-1.5 bg-legend-deep hover:bg-legend-navy border border-legend-teal/30 text-legend-paper rounded-lg text-xs"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      {error && (
        <div className="shrink-0 mx-4 mt-3 rounded-lg border border-red-500/40 bg-red-950/40 px-4 py-2 text-sm text-red-100 flex justify-between gap-2">
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} className="text-red-300 text-xs shrink-0">
            Dismiss
          </button>
        </div>
      )}

      {showNewChatConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-legend-deep/80 px-4">
          <div
            className="max-w-md w-full rounded-lg border border-legend-teal/30 bg-legend-navy p-5 shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-chat-confirm-title"
          >
            <h2 id="new-chat-confirm-title" className="text-base font-semibold text-legend-paper mb-3">
              Analysis in progress
            </h2>
            <p className="text-sm text-gray-300 leading-relaxed">
              Running multiple analysis at once will make both go slower. You will need to go to HISTORY and return
              to this chat to see the report. Do you want to continue?
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowNewChatConfirm(false)}
                className="px-4 py-2 rounded-lg bg-gray-800 border border-gray-600 text-gray-200 text-sm hover:bg-gray-700"
              >
                No
              </button>
              <button
                type="button"
                onClick={handleConfirmNewChat}
                className="px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-500 text-white text-sm font-medium"
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="app-workspace">
        <aside className={`${showKb ? 'hidden lg:flex' : 'hidden'} w-full lg:w-96 xl:w-[28rem] shrink-0 border-r border-legend-teal/20 bg-legend-navy flex-col min-h-0 overflow-hidden lg:sticky lg:top-14 lg:max-h-[calc(100dvh-3.5rem)]`}>
          <div className="app-scroll-pane p-4">
            <KnowledgePanel
              uiLanguage={uiLanguage}
              stats={kbStats}
              syncing={kbSyncing}
              onSync={onKbSync}
              onReindex={onKbReindex}
              ingestUrl={ingestUrl}
              onIngestUrlChange={onIngestUrlChange}
              onIngestUrl={onIngestUrl}
              ingesting={ingestingUrl}
              isPlatformAdmin={isSuperadminRole(authProfile?.role)}
              isOrgAdmin={isOrgAdminRole(authProfile?.role)}
              canSyncKb={canSyncKb(authProfile)}
              canFeedLlm={canFeedLlm(authProfile)}
              canManageNoindex={canManageKbNoindex(authProfile)}
              feedFileAccept={getFeedFileAccept(authProfile)}
              onUploadFiles={onKbUpload}
              uploading={kbUploading}
              uploadFeedback={kbUploadFeedback}
              uploadVisible={kbUploadVisible}
              onUploadVisibleChange={onKbUploadVisibleChange}
              kbFiles={kbStats?.files || []}
              onToggleNoindex={onKbNoindexToggle}
              onDeleteFile={onKbDelete}
              noindexUpdatingPath={kbNoindexUpdatingPath}
              deletingPath={kbDeletingPath}
              compactLayout
            />
          </div>
        </aside>

        {showHistory && (
          <aside className="w-full md:w-72 shrink-0 border-r border-legend-teal/20 bg-legend-navy flex flex-col min-h-0 overflow-hidden lg:sticky lg:top-14 lg:max-h-[calc(100dvh-3.5rem)]">
            <div className="px-4 py-3 border-b border-legend-teal/15 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-legend-paper">History</h2>
                <p className="text-[11px] text-gray-500 mt-0.5">Open a session or delete individual entries.</p>
              </div>
              {chatHistory.length > 0 && (
                <button
                  type="button"
                  onClick={onClearHistory}
                  className="text-xs text-red-400/90 hover:text-red-300 shrink-0"
                  title={tUi(uiLanguage, 'appConfirmClearAllHistory')}
                >
                  {tUi(uiLanguage, 'appClearAllHistory')}
                </button>
              )}
            </div>
            <div className="app-scroll-pane p-3 space-y-2">
              {historyLoading ? (
                <p className="text-sm text-gray-500 px-2">Loading…</p>
              ) : chatHistory.length === 0 ? (
                <p className="text-sm text-gray-500 px-2">No saved sessions yet.</p>
              ) : (
                chatHistory.map((item) => (
                  <div
                    key={item.session_id}
                    onClick={() => onSelectConversation(item.session_id)}
                    onKeyDown={(e) => e.key === 'Enter' && onSelectConversation(item.session_id)}
                    role="button"
                    tabIndex={0}
                    className={`w-full text-left rounded-lg p-3 border transition-all duration-200 cursor-pointer ${
                      item.session_id === sessionId
                        ? 'bg-legend-teal/15 border-legend-teal text-legend-paper'
                        : 'bg-legend-deep border-legend-teal/20 text-legend-paper hover:bg-legend-navy'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold truncate">{item.title || 'Untitled'}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {formatHistoryTimestamp(item.updated_at)} · {item.message_count} msgs
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => onDeleteConversation(item.session_id, e)}
                        className="text-gray-500 hover:text-red-400 p-1 shrink-0"
                        title={tUi(uiLanguage, 'appDeleteChatTitle')}
                        aria-label={tUi(uiLanguage, 'appDeleteChatTitle')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </aside>
        )}

        <div className="app-workspace-main">
          {showKb && (
          <div className="lg:hidden shrink-0 border-b border-legend-teal/20 p-4">
            <KnowledgePanel
              uiLanguage={uiLanguage}
              stats={kbStats}
              syncing={kbSyncing}
              onSync={onKbSync}
              onReindex={onKbReindex}
              ingestUrl={ingestUrl}
              onIngestUrlChange={onIngestUrlChange}
              onIngestUrl={onIngestUrl}
              ingesting={ingestingUrl}
              isPlatformAdmin={isSuperadminRole(authProfile?.role)}
              isOrgAdmin={isOrgAdminRole(authProfile?.role)}
              canSyncKb={canSyncKb(authProfile)}
              canFeedLlm={canFeedLlm(authProfile)}
              canManageNoindex={canManageKbNoindex(authProfile)}
              feedFileAccept={getFeedFileAccept(authProfile)}
              onUploadFiles={onKbUpload}
              uploading={kbUploading}
              uploadFeedback={kbUploadFeedback}
              uploadVisible={kbUploadVisible}
              onUploadVisibleChange={onKbUploadVisibleChange}
              kbFiles={kbStats?.files || []}
              onToggleNoindex={onKbNoindexToggle}
              onDeleteFile={onKbDelete}
              noindexUpdatingPath={kbNoindexUpdatingPath}
              deletingPath={kbDeletingPath}
              compactLayout
            />
          </div>
          )}
          <SimplifiedTier1Workspace
            apiFetch={apiFetch}
            sessionId={sessionId}
            screenLanguage={screenLanguage}
            reportLanguage={reportLanguage}
            onScreenLanguageChange={onScreenLanguageChange}
            onReportLanguageChange={onReportLanguageChange}
            onSaveLanguages={onSaveLanguages}
            onError={(msg) => (msg ? setError(msg) : setError(null))}
            savedReportHtml={savedReportHtml}
            savedReportTitle={savedReportTitle}
            savedUploadedFiles={savedUploadedFiles}
            onUploadedFilesChange={onUploadedFilesChange}
            savedTier1Draft={savedTier1Draft}
            onTier1DraftChange={onTier1DraftChange}
            savedReportChatMessages={savedReportChatMessages}
            onReportChatMessagesChange={onReportChatMessagesChange}
            onConversationRefresh={onConversationRefresh}
            onReportHtmlUpdated={onReportHtmlUpdated}
            onAnalysisRunningChange={setAnalysisRunning}
          />
        </div>
      </div>
    </div>
  );
}
